import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['area', 'input', 'toggle', 'name'];
  static values = {
    dropText: { type: String, default: 'Drop your file here!' }
  };

  connect() {
    this.dragCounter = 0;

    this.onDragEnter = this.onDragEnter.bind(this);
    this.onDragLeave = this.onDragLeave.bind(this);
    this.onDragOver = this.onDragOver.bind(this);
    this.onDrop = this.onDrop.bind(this);

    document.addEventListener('dragenter', this.onDragEnter);
    document.addEventListener('dragleave', this.onDragLeave);
    document.addEventListener('dragover', this.onDragOver);
    document.addEventListener('drop', this.onDrop);

    this.syncDropText();
  }

  disconnect() {
    document.removeEventListener('dragenter', this.onDragEnter);
    document.removeEventListener('dragleave', this.onDragLeave);
    document.removeEventListener('dragover', this.onDragOver);
    document.removeEventListener('drop', this.onDrop);

    this.dragCounter = 0;
    document.body.classList.remove('dragging');
  }

  // ── Values changed callback ──

  dropTextValueChanged() {
    this.syncDropText();
  }

  // ── Document-level drag tracking (counter strategy) ──

  onDragEnter(event) {
    event.preventDefault();
    this.dragCounter++;
    if (this.dragCounter === 1) {
      document.body.classList.add('dragging');
    }
  }

  onDragLeave(event) {
    event.preventDefault();
    this.dragCounter--;
    if (this.dragCounter <= 0) {
      this.dragCounter = 0;
      document.body.classList.remove('dragging');
    }
  }

  onDragOver(event) {
    // Required to allow drop
    event.preventDefault();
  }

  onDrop(event) {
    // Reset drag state for any drop (even outside an area target)
    event.preventDefault();
    this.dragCounter = 0;
    document.body.classList.remove('dragging');
  }

  // ── Area-level actions (wired via data-action in HTML) ──

  dragOver(event) {
    event.preventDefault();
    event.currentTarget.classList.add('drag-over');
  }

  dragEnter(event) {
    event.preventDefault();
    event.currentTarget.classList.add('drag-over');
  }

  dragLeave(event) {
    event.preventDefault();
    event.currentTarget.classList.remove('drag-over');
  }

  drop(event) {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.classList.remove('drag-over');

    const files = event.dataTransfer?.files;
    if (!files || files.length === 0) return;

    // Assign dropped files to the file input
    if (this.hasInputTarget) {
      this.inputTarget.files = files;
    }

    // Apply the optional toggle param from the drop zone ("true"/"false" from Stimulus)
    if (this.hasToggleTarget && event.params?.toggle !== undefined) {
      const toggleParam = event.params.toggle;
      const toggleChecked = toggleParam === true || toggleParam === 'true' ? true : toggleParam === false || toggleParam === 'false' ? false : null;

      if (toggleChecked !== null) {
        this.toggleTarget.checked = toggleChecked;
      }
    }

    // Populate name from filename before auto-submit fires
    this._populateNameFromFile();

    // Dispatch change to trigger auto-submit via form controller
    if (this.hasInputTarget) {
      this.inputTarget.dispatchEvent(new Event('change', { bubbles: true }));
    }

    // Clean up global drag state
    this.dragCounter = 0;
    document.body.classList.remove('dragging');
  }

  // ── File picker ──

  openFilePicker() {
    if (this.hasInputTarget) {
      this.inputTarget.click();
    }
  }

  fileSelected() {
    this._populateNameFromFile();
  }

  // ── Private ──

  syncDropText() {
    this.areaTargets.forEach((area) => {
      if (!area.hasAttribute('data-drop-text')) {
        area.setAttribute('data-drop-text', this.dropTextValue);
      }
    });
  }

  _populateNameFromFile() {
    if (!this.hasInputTarget || !this.hasNameTarget) return;
    const file = this.inputTarget.files[0];
    if (file && !this.nameTarget.value) {
      this.nameTarget.value = file.name.replace(/\.[^.]+$/, '');
    }
  }
}

